import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Tag, ArrowRight } from "lucide-react";
import { api, fdate } from "../../lib/api";

const quoteLink = (line, deal) => `/quote?kind=cruise&line=${encodeURIComponent(line)}${deal ? `&offer=${encodeURIComponent(deal)}` : ""}`;

const Deal = ({ line, d }) => (
  <li className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`cruise-deal-${d.id}`}>
    <p className="flex items-start gap-2 font-semibold text-slate-900"><Tag className="mt-1 h-4 w-4 shrink-0 text-[var(--accent,#b8860b)]" />{d.title}</p>
    {d.summary && <p className="mt-1 text-sm text-slate-600">{d.summary}</p>}
    <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs">
      <span className="text-slate-500">{d.ends_on ? `Ends ${fdate(d.ends_on)}` : "Limited time"}</span>
      <Link to={quoteLink(line, d.title)} className="inline-flex items-center gap-1 font-semibold text-slate-900 underline-offset-4 hover:underline" data-testid={`cruise-deal-ask-${d.id}`}>Ask about this offer <ArrowRight className="h-3 w-3" /></Link>
    </div>
  </li>
);

const LineCard = ({ ln, item }) => (
  <article className="flex flex-col overflow-hidden rounded-2xl bg-slate-50 ring-1 ring-slate-200" data-testid={`cruise-line-${ln.key}`}>
    <div className="relative aspect-[16/10] overflow-hidden">
      {item?.image && <img src={item.image} alt={item.image_alt || ln.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 hover:scale-105" />}
      <span className="absolute left-4 top-4 rounded-full bg-white/90 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-slate-800">{ln.kind}</span>
    </div>
    <div className="flex flex-1 flex-col p-6">
      <h3 className="font-display text-2xl font-semibold">{ln.name}</h3>
      {item?.text && <p className="mt-2 text-sm text-slate-600">{item.text}</p>}
      <h4 className="mt-5 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Current offers</h4>
      {ln.deals.length ? <ul className="mt-3 space-y-3">{ln.deals.map((d) => <Deal key={d.id} line={ln.name} d={d} />)}</ul>
        : <p className="mt-3 text-sm text-slate-500" data-testid={`cruise-line-${ln.key}-nodeals`}>Ask us about this line's latest offers.</p>}
      <div className="mt-auto pt-6"><Link to={quoteLink(ln.name)} className="btn-outline" data-testid={`cruise-line-${ln.key}-inquire`}>Plan a {ln.name} cruise</Link></div>
    </div>
  </article>
);

export const CruiseLines = ({ d }) => {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/public/cruise-deals").then((r) => setData(r.data)).catch(() => setData({ lines: [] })); }, []);
  const items = d.items || [];
  return (
    <section className="container-x py-20" data-testid="cruise-lines-block">
      {d.eyebrow && <p className="text-xs font-semibold uppercase tracking-[0.25em] text-slate-500">{d.eyebrow}</p>}
      {d.heading && <h2 className="mt-2 max-w-2xl font-display text-4xl font-semibold sm:text-5xl">{d.heading}</h2>}
      {d.text && <p className="mt-4 max-w-2xl text-slate-600">{d.text}</p>}
      <div className="mt-12 grid gap-8 md:grid-cols-2">
        {(data?.lines || []).map((ln) => <LineCard key={ln.key} ln={ln} item={items.find((i) => i.line === ln.key)} />)}
      </div>
      <p className="mt-8 text-xs text-slate-500" data-testid="cruise-deals-disclaimer">Offers are summarized from each cruise line's website{data?.checked_at ? `, last checked ${fdate(data.checked_at)}` : ""}. They are subject to change, availability and the cruise line's terms. Ask us for full details before booking.</p>
    </section>
  );
};
